import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ShippingService } from 'src/app/features/devices/services/shipping.service';
import {
  DELIVERABILITY_LABELS,
  DeliverabilityResult,
  DeliverabilityStatus,
} from 'src/app/features/devices/services/shipping.type';
import { SuggestedAddress } from 'src/app/shared/address-autocomplete/address-from-place';
import { AddressSuggestionsComponent } from 'src/app/shared/address-autocomplete/address-suggestions.component';
import { COMMON_COUNTRY_CODES, COUNTRIES, Country, countryName } from 'src/app/shared/constants/countries';

/** The patient's shipping address as a form group. Shared by the new-patient wizard and the patient page. */
export function shippingAddressGroup(): FormGroup {
  return new FormGroup({
    street: new FormControl(''),
    city: new FormControl(''),
    stateOrProvince: new FormControl(''),
    postalCode: new FormControl(''),
    // Free text from before the country picker existed. Kept so an untouched
    // address saves as it was; the API works the ISO code out from it.
    country: new FormControl(''),
    countryAlpha2: new FormControl<string | null>(null),
  });
}

/** Form values for a stored address. */
export function shippingAddressFormValue(address: any): Record<string, string | null> {
  return {
    street: address?.street ?? '',
    city: address?.city ?? '',
    stateOrProvince: address?.stateOrProvince ?? '',
    postalCode: address?.postalCode ?? '',
    country: address?.country ?? '',
    countryAlpha2: address?.countryAlpha2?.trim() || null,
  };
}

/**
 * The address as the API takes it. An unpicked country goes as `null`, never
 * `''` — the API reads a string as a code to validate.
 */
export function shippingAddressPayload(value: any): Record<string, string | null> {
  return { ...(value ?? {}), countryAlpha2: value?.countryAlpha2 || null };
}

function sortedCountries(): Country[] {
  const common = COMMON_COUNTRY_CODES.map((code) => COUNTRIES.find((c) => c.alpha2 === code)).filter(
    (c): c is Country => !!c
  );
  return [...common, ...COUNTRIES.filter((c) => !COMMON_COUNTRY_CODES.includes(c.alpha2))];
}

/**
 * A patient's shipping address, and — for the shipping roles, when Easyship is
 * on and the lab has switched the check on — whether Easyship can deliver to it.
 *
 * The check is a warning, never a gate: a patient is saved whatever it says.
 * Anyone outside the shipping roles sees the address fields only and nothing is
 * asked from here; the API checks their saves in the background instead.
 */
@Component({
  selector: 'app-patient-shipping-address',
  templateUrl: './patient-shipping-address.component.html',
  styleUrls: ['./patient-shipping-address.component.scss'],
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, AddressSuggestionsComponent],
})
export class PatientShippingAddressComponent implements OnInit, OnDestroy {
  @Input({ required: true }) group!: FormGroup;

  readonly countries = sortedCountries();
  readonly labels = DELIVERABILITY_LABELS;

  /** Whether this user and this lab check addresses at all. */
  checksEnabled = false;
  checking = false;
  result: DeliverabilityResult | null = null;

  /** The address the last answer was about, so pressing Next twice asks once. */
  private checkedKey: string | null = null;
  private changes?: Subscription;

  constructor(
    public ability: AbilityService,
    private shipping: ShippingService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.changes = this.group.valueChanges.subscribe(() => {
      if (this.checkedKey !== this.key()) {
        this.result = null;
      }
    });
    if (!this.ability.canUseEasyship()) {
      return;
    }
    this.shipping.getPreferences().subscribe({
      next: (preferences) => (this.checksEnabled = !!preferences?.patientAddressCheck),
      error: () => (this.checksEnabled = false),
    });
  }

  ngOnDestroy(): void {
    this.changes?.unsubscribe();
  }

  /** The free text an older record holds, shown while no country is picked. */
  get legacyCountry(): string | null {
    const text = (this.group.value?.country ?? '').trim();
    if (!text || this.group.value?.countryAlpha2) {
      return null;
    }
    return text;
  }

  get statusLabel(): string {
    return this.result ? this.labels[this.result.status] : '';
  }

  tone(status: DeliverabilityStatus): 'ok' | 'bad' | 'warn' | 'muted' {
    if (status === 'DELIVERABLE') return 'ok';
    if (status === 'NOT_DELIVERABLE' || status === 'INVALID') return 'bad';
    if (status === 'INCOMPLETE') return 'warn';
    return 'muted';
  }

  /**
   * Ask whether Easyship can deliver here. Does nothing for other roles, when
   * the lab has it switched off, for an empty address, or for one already asked
   * about. Never blocks the form: the answer arrives while the person carries on.
   *
   * `announce` also says it in a toast, for when the form is no longer on screen.
   */
  check(announce = true): void {
    if (!this.checksEnabled || this.checking) {
      return;
    }
    const value = this.group.value ?? {};
    const filled = ['street', 'city', 'stateOrProvince', 'postalCode', 'countryAlpha2'].some((field) =>
      `${value[field] ?? ''}`.trim()
    );
    const key = this.key();
    if (!filled || key === this.checkedKey) {
      return;
    }

    this.checking = true;
    this.shipping
      .checkAddress({
        line1: value.street,
        city: value.city,
        state: value.stateOrProvince,
        postalCode: value.postalCode,
        countryAlpha2: value.countryAlpha2,
      })
      .subscribe({
        next: (result) => {
          this.checking = false;
          this.result = result;
          this.checkedKey = key;
          if (announce) {
            this.announce(result);
          }
        },
        error: () => {
          this.checking = false;
        },
      });
  }

  countryLabel(code: string | null): string {
    return countryName(code) ?? '';
  }

  /**
   * An address picked from Google's suggestions fills the address, every field
   * still editable. Like any other edit it clears an earlier Easyship answer:
   * a suggestion says the address exists, not that a courier delivers there.
   */
  applySuggestion(address: SuggestedAddress): void {
    const filled = {
      street: address.line1,
      city: address.city,
      stateOrProvince: address.state,
      postalCode: address.postalCode,
      countryAlpha2: address.countryAlpha2 || this.group.value?.countryAlpha2 || null,
    };
    this.group.patchValue(filled);
    for (const name of Object.keys(filled)) {
      this.group.get(name)?.markAsDirty();
    }
  }

  private announce(result: DeliverabilityResult): void {
    if (result.status === 'DELIVERABLE') {
      this.notification.success('Shipping address: Easyship can deliver here.');
    } else if (result.status === 'NOT_DELIVERABLE' || result.status === 'INVALID') {
      this.notification.error(`Shipping address: ${result.message} The patient can still be saved.`);
    }
  }

  private key(): string {
    const value = this.group?.value ?? {};
    return JSON.stringify([value.street, value.city, value.stateOrProvince, value.postalCode, value.countryAlpha2]);
  }
}
